import { File, FileArchive, FileSpreadsheet, FileText, Image, type LucideIcon } from "lucide-react";

/**
 * What an attachment icon/label pair should look like.
 *
 * Two very different attachment sources feed this: app-owned attachments are bare `/static/…`
 * path strings with no mime type at all (the API renames every upload to a random UUID, so there
 * is no human filename either — see `packages/contract/src/appointment/appointment.ts`), while
 * Immotion attachments carry a real `mime` and `filename`. `describeFile` normalises both into one
 * shape so a single attachment list can render either without knowing which source it came from.
 */
export type FileKind = "image" | "pdf" | "doc" | "sheet" | "archive" | "other";

export interface FileDescriptor {
  kind: FileKind;
  /** Lowercase, no leading dot. Empty string when nothing could be parsed. */
  ext: string;
  Icon: LucideIcon;
  /** Tailwind text-color class for the icon, one per kind — the tinted-square treatment. */
  tint: string;
}

const KIND_BY_EXT: Record<string, FileKind> = {
  jpg: "image", jpeg: "image", png: "image", gif: "image", webp: "image", heic: "image", heif: "image", svg: "image", bmp: "image",
  pdf: "pdf",
  doc: "doc", docx: "doc", txt: "doc", rtf: "doc",
  xls: "sheet", xlsx: "sheet", csv: "sheet",
  zip: "archive", rar: "archive", "7z": "archive",
};

const ICON_BY_KIND: Record<FileKind, LucideIcon> = {
  image: Image,
  pdf: FileText,
  doc: FileText,
  sheet: FileSpreadsheet,
  archive: FileArchive,
  other: File,
};

const TINT_BY_KIND: Record<FileKind, string> = {
  image: "text-emerald-600",
  pdf: "text-red-600",
  doc: "text-blue-600",
  sheet: "text-green-700",
  archive: "text-amber-600",
  other: "text-slate-500",
};

function kindFromMime(mime: string): FileKind | null {
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (mime.includes("spreadsheet") || mime.includes("excel") || mime === "text/csv") return "sheet";
  if (mime.includes("word") || mime === "text/plain" || mime.includes("rtf")) return "doc";
  if (mime.includes("zip") || mime.includes("compressed") || mime.includes("archive")) return "archive";
  return null;
}

function extFromPath(path: string): string {
  const clean = path.split(/[?#]/)[0];
  const match = clean.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toLowerCase() : "";
}

/**
 * Resolves an icon/kind/extension for an attachment. Prefers `mime` when present (Immotion
 * attachments); falls back to the extension parsed off `url` or `filename` (app attachments,
 * which carry neither a mime type nor a real filename).
 */
export function describeFile(input: {
  url?: string | null;
  mime?: string | null;
  filename?: string | null;
}): FileDescriptor {
  const fromMime = input.mime ? kindFromMime(input.mime) : null;
  const ext = extFromPath(input.filename || input.url || "");
  const kind = fromMime ?? KIND_BY_EXT[ext] ?? "other";

  return { kind, ext, Icon: ICON_BY_KIND[kind], tint: TINT_BY_KIND[kind] };
}
